import { Fragment } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import TextContentSep from '../common/TextContentSep';
import styles from './SampleListStatsCards.module.css';

export interface SampleListStats {
  total: number;
  available: number;
  outOfStock: number;
  /** 一覧 API のみ。未指定の呼び出し元（帳票等）では廃棄カードを出さない */
  disposed?: number;
}

export type SampleListStatId = 'total' | 'available' | 'outOfStock' | 'disposed';

export type SampleListStatsTitleOverrides = Partial<Record<SampleListStatId, string>>;

interface SampleListStatsCardsProps {
  stats: SampleListStats;
  /** 絞り込みなし（filtersKey === '{}'）のときのみ「総数」系ラベル */
  unfiltered: boolean;
  isAdmin: boolean;
  /** 一覧以外（レポート等）でラベルを差し替える */
  titleOverrides?: SampleListStatsTitleOverrides;
  /** 説明文（デスクトップのみ表示）。レポートでは false */
  showDescriptions?: boolean;
  /** 一覧向け：1行ストリップで表までの距離を短縮 */
  compact?: boolean;
  /** compact 時: クリックでステータス絞り込み */
  onStatSelect?: (id: SampleListStatId) => void;
  /** compact 時: 現在選択中のストリップ */
  activeStat?: SampleListStatId;
}

type StatCardDef = {
  id: SampleListStatId;
  gradientClass: string;
  toneClass: string;
  icon: string;
  valueKey: keyof SampleListStats;
  titleKey?: string;
  shortTitleKey?: string;
  /** compact ストリップ用（xs で「利用可」が「利…」に潰れるのを防ぐ） */
  chipTitleKey?: string;
  descKey?: string;
};

const CARDS: StatCardDef[] = [
  {
    id: 'total',
    gradientClass: styles.gradientTotal,
    toneClass: styles.stripTotal,
    icon: 'fa-vial',
    valueKey: 'total',
  },
  {
    id: 'available',
    gradientClass: styles.gradientAvailable,
    toneClass: styles.stripAvailable,
    icon: 'fa-check-circle',
    valueKey: 'available',
    titleKey: 'sample.stats_available',
    shortTitleKey: 'sample.stats_available_short',
    chipTitleKey: 'sample.stats_available_chip',
    descKey: 'sample.stats_available_desc',
  },
  {
    id: 'outOfStock',
    gradientClass: styles.gradientOutOfStock,
    toneClass: styles.stripOutOfStock,
    icon: 'fa-exclamation-triangle',
    valueKey: 'outOfStock',
    titleKey: 'sample.stats_out_of_stock',
    shortTitleKey: 'sample.stats_out_of_stock_short',
    chipTitleKey: 'sample.stats_out_of_stock_chip',
    descKey: 'sample.stats_out_of_stock_desc',
  },
  {
    id: 'disposed',
    gradientClass: styles.gradientDisposed,
    toneClass: styles.stripDisposed,
    icon: 'fa-trash-alt',
    valueKey: 'disposed',
    titleKey: 'sample.stats_disposed',
    shortTitleKey: 'sample.stats_disposed_short',
    chipTitleKey: 'sample.stats_disposed_chip',
    descKey: 'sample.stats_disposed_desc',
  },
];

function visibleCards(stats: SampleListStats, activeStat?: SampleListStatId): StatCardDef[] {
  return CARDS.filter((card) => {
    // 総数は常時表示（ステータス絞り込み中は「該当」件数＋クリックで解除の導線）
    if (card.id === 'disposed') {
      if (stats.disposed == null) return false;
      return stats.disposed > 0 || activeStat === 'disposed';
    }
    if (card.id === 'outOfStock') {
      return (stats.outOfStock ?? 0) > 0 || activeStat === 'outOfStock';
    }
    return true;
  });
}

function colSize(count: number): { xs: number; md: number } {
  if (count >= 4) return { xs: 6, md: 3 };
  return { xs: 4, md: 4 };
}

export default function SampleListStatsCards({
  stats,
  unfiltered,
  isAdmin,
  titleOverrides,
  showDescriptions = true,
  compact = false,
  onStatSelect,
  activeStat,
}: SampleListStatsCardsProps) {
  const { t } = useTranslation();
  const cards = visibleCards(stats, activeStat);

  // total / my / matching の title・short・chip を同じ語幹から解決
  const totalScope = unfiltered ? (isAdmin ? 'total' : 'my') : 'matching';
  const totalBase = `sample.stats_${totalScope}_samples`;
  const defaultTotalTitle = t(totalBase);
  const defaultTotalShort = t(`${totalBase}_short`, { defaultValue: defaultTotalTitle });
  const defaultTotalChip = t(`${totalBase}_chip`, { defaultValue: defaultTotalShort });

  const resolveTitles = (card: StatCardDef) => {
    // titleOverrides は tooltip / カード長名のみ。compact 短名は shortTitleKey を崩さない
    const title =
      titleOverrides?.[card.id] ??
      (card.titleKey ? t(card.titleKey) : defaultTotalTitle);
    const shortTitle = card.shortTitleKey
      ? t(card.shortTitleKey, { defaultValue: title })
      : defaultTotalShort;
    const chipTitle = card.chipTitleKey
      ? t(card.chipTitleKey, { defaultValue: shortTitle })
      : card.id === 'total'
        ? defaultTotalChip
        : shortTitle;
    return { title, shortTitle, chipTitle };
  };

  const renderStripItem = (card: StatCardDef, interactive: boolean) => {
    const { title, chipTitle } = resolveTitles(card);
    const isActive = (activeStat ?? 'total') === card.id;
    const className = [
      styles.stripItem,
      card.toneClass,
      interactive ? styles.stripButton : '',
      isActive ? styles.stripActive : '',
    ]
      .filter(Boolean)
      .join(' ');
    const value = stats[card.valueKey] ?? 0;
    const valueText = String(value);
    // 可視＝chip 短名＋件数。aria/title は中黒連結（詳細説明は title）
    const ariaLabel = joinLabeledValue(chipTitle, valueText);
    const controlTitle = joinLabeledValue(title, valueText);
    if (!interactive) {
      return (
        <div className={className} title={controlTitle} aria-label={ariaLabel}>
          <i className={`fas ${card.icon}`} aria-hidden="true" />
          <span className={styles.stripLabel}>{chipTitle}</span>
          <TextContentSep />
          <span className={styles.stripValue}>{value}</span>
        </div>
      );
    }
    return (
      <button
        type="button"
        className={className}
        title={controlTitle}
        aria-label={ariaLabel}
        aria-pressed={isActive}
        onClick={() => onStatSelect?.(card.id)}
      >
        <i className={`fas ${card.icon}`} aria-hidden="true" />
        <span className={styles.stripLabel} aria-hidden="true">
          {chipTitle}
        </span>
        <TextContentSep />
        <span className={styles.stripValue} aria-hidden="true">
          {value}
        </span>
      </button>
    );
  };

  if (compact) {
    const interactive = typeof onStatSelect === 'function';
    return (
      <div
        className={`${styles.strip} sample-list-stats mb-0`}
        role="group"
        aria-label={t('sample.samples')}
      >
        {cards.map((card, index) => (
          <Fragment key={card.id}>
            {index > 0 ? <TextContentSep /> : null}
            {renderStripItem(card, interactive)}
          </Fragment>
        ))}
      </div>
    );
  }

  const cols = colSize(cards.length);
  return (
    <Row className="mb-3 g-2 g-md-3">
      {cards.map((card) => {
        const { title, shortTitle } = resolveTitles(card);
        return (
          <Col xs={cols.xs} md={cols.md} key={card.id}>
            <Card className={`h-100 border-0 shadow-sm ${styles.card}`}>
              <Card.Body className={`text-white rounded ${card.gradientClass} ${styles.body}`}>
                <div className={styles.content}>
                  <div className={styles.textBlock}>
                    <Card.Title className={`mb-1 text-white-50 fw-semibold ${styles.title}`} title={title}>
                      <i className={`fas ${card.icon} me-1 d-none d-md-inline`} aria-hidden="true" />
                      <span className="d-none d-md-inline">{title}</span>
                      <span className="d-md-none">{shortTitle}</span>
                    </Card.Title>
                    <div className={styles.value}>{stats[card.valueKey] ?? 0}</div>
                    {showDescriptions && card.descKey ? (
                      <div className={`small text-white-50 ${styles.desc}`}>{t(card.descKey)}</div>
                    ) : null}
                  </div>
                  <div className={styles.decorIcon} aria-hidden="true">
                    <i className={`fas ${card.icon}`} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}
